import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { Line, LineChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import { useHistory } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { db, auth } from "../Config/firebase";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";
import { gerarSequencial, obterMaiorSequencial } from "../Utils/sequencial";
import {
  COLLECTION_CAPSULAS,
  COLLECTION_CILINDROS,
  COLLECTION_COMPACTACAO,
  calcularPontoCompactacao,
  criarCBR,
  criarExpansao,
  criarGranulometria,
  criarLimites,
  criarPontosCompactacao,
  estadoInicialCompactacao,
  formatarDataBR,
  numero,
  penetracoesCBR,
  recalcularResultados,
  sincronizarDatasExpansao,
} from "./compactacaoModel";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";
import RelatorioCompactacaoPrint from "./RelatorioCompactacaoPrint";

function normalizar(valor) {
  return String(valor || "").toLowerCase();
}

function calcularProximoCodigo(lista) {
  const ano = new Date().getFullYear();
  const numeros = lista
    .map((item) => String(item.codigo || "").match(/RE-EC-(\d+)\/\d{4}/))
    .filter(Boolean)
    .map((match) => Number(match[1]))
    .filter(Number.isFinite);
  const proximo = numeros.length ? Math.max(...numeros) + 1 : lista.length + 1;
  return `RE-EC-${String(proximo).padStart(3, "0")}/${ano}`;
}

function campoTabela(valor, onChange, props = {}) {
  const { inputProps = {}, onKeyDown, ...restProps } = props;

  return (
    <TextField
      {...restProps}
      value={valor || ""}
      onChange={(event) => onChange(event.target.value)}
      variant="standard"
      fullWidth
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented || event.key !== "Enter") return;

        event.preventDefault();
        const celulaAtual = event.currentTarget.closest("td");
        const indiceCelula = celulaAtual?.cellIndex;
        let linhaSeguinte = celulaAtual?.closest("tr")?.nextElementSibling;

        while (linhaSeguinte && Number.isInteger(indiceCelula)) {
          const campoAbaixo = linhaSeguinte.cells?.[indiceCelula]?.querySelector("input:not([disabled]):not([readonly])");
          if (campoAbaixo) {
            campoAbaixo.focus();
            campoAbaixo.select?.();
            break;
          }
          linhaSeguinte = linhaSeguinte.nextElementSibling;
        }
      }}
      inputProps={{ style: { textAlign: restProps.align || "center", fontSize: 13 }, ...inputProps }}
    />
  );
}

function selecionarPorId(lista, id) {
  return lista.find((item) => item.id === id) || null;
}

function graficoVazio(texto) {
  return (
    <Box sx={{ height: 260, display: "grid", placeItems: "center", color: "text.secondary", border: "1px dashed #cbd5e1" }}>
      {texto}
    </Box>
  );
}

export default function CadastroCompactacao() {
  const history = useHistory();
  const [ensaios, setEnsaios] = useState([]);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [cilindros, setCilindros] = useState([]);
  const [capsulas, setCapsulas] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [open, setOpen] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [itemEditando, setItemEditando] = useState(null);
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(estadoInicialCompactacao);
  const [visualizando, setVisualizando] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const salvandoRef = useRef(false);

  useEffect(() => {
    carregarDados();
  }, []);

  async function carregarDados() {
    const [ensaiosSnap, clientesSnap, cilindrosSnap, capsulasSnap, materiaisSnap] = await Promise.all([
      getDocs(consultaTenantAtual(query(collection(db, COLLECTION_COMPACTACAO), orderBy("criadoEm", "desc")))),
      getDocs(consultaTenantAtual(query(collection(db, "clientes"), orderBy("nome", "asc")))),
      getDocs(consultaTenantAtual(query(collection(db, COLLECTION_CILINDROS), orderBy("numero", "asc")))),
      getDocs(consultaTenantAtual(query(collection(db, COLLECTION_CAPSULAS), orderBy("numero", "asc")))),
      getDocs(consultaTenantAtual(query(collection(db, "tblListaMateriaisCadastrados")))),
    ]);
    const cilindrosCarregados = cilindrosSnap.docs.map((item) => ({ ...item.data(), id: item.id }));
    setEnsaios(ensaiosSnap.docs.map((item) => ({ ...item.data(), id: item.id })));
    setClientes(clientesSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setCilindros(cilindrosCarregados);
    setCapsulas(capsulasSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setMateriais(
      materiaisSnap.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) =>
          String(a.codigo || "").localeCompare(String(b.codigo || ""), "pt-BR", { numeric: true })
        )
    );
    setCarregandoLista(false);
  }

  const materiaisDisponiveis = useMemo(() => {
    if (!form.clienteId || !form.obraId) return [];

    return materiais.filter((material) => {
      const materialDeSolo =
        material.IsSolos === true || normalizar(material.CategoriaMaterial) === "solos";
      const mesmoCliente =
        !material.ClienteId ||
        material.ClienteId === form.clienteId ||
        normalizar(material.Cliente) === normalizar(form.cliente);
      const mesmaObra = material.TodasObras === true || material.ObraId === form.obraId;

      return materialDeSolo && mesmoCliente && mesmaObra;
    });
  }, [materiais, form.clienteId, form.cliente, form.obraId]);

  const materialSelecionado = useMemo(
    () =>
      materiaisDisponiveis.find((material) => material.id === form.materialId) ||
      materiaisDisponiveis.find(
        (material) => normalizar(material.Descricao) === normalizar(form.material)
      ) ||
      null,
    [materiaisDisponiveis, form.materialId, form.material]
  );

  async function carregarObras(clienteId) {
    if (!clienteId) {
      setObras([]);
      return;
    }
    const snap = await getDocs(consultaTenantAtual(query(collection(db, "obras"), where("clienteId", "==", clienteId), orderBy("nome", "asc"))));
    setObras(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
  }

  function abrirNovo() {
    setModoEdicao(false);
    setItemEditando(null);
    setTab(0);
    setObras([]);
    setForm({
      ...estadoInicialCompactacao,
      compactacao: criarPontosCompactacao(),
      expansao: criarExpansao(),
      cbr: criarCBR(),
      granulometria: {
        ...estadoInicialCompactacao.granulometria,
        peneiramento: criarGranulometria(),
      },
      limites: criarLimites(),
    });
    setOpen(true);
  }

  function editar(item) {
    setModoEdicao(true);
    setItemEditando(item);
    setTab(0);
    const itemComDatasExpansao = { ...estadoInicialCompactacao, ...item };
    setForm({
      ...itemComDatasExpansao,
      expansao: sincronizarDatasExpansao(itemComDatasExpansao.expansao, itemComDatasExpansao.dataEnsaio),
    });
    carregarObras(item.clienteId);
    setOpen(true);
  }

  function atualizarFormulario(parcial) {
    const proximo = { ...form, ...parcial };
    const proximoComDatasExpansao = {
      ...proximo,
      expansao: sincronizarDatasExpansao(proximo.expansao, proximo.dataEnsaio),
    };
    setForm({
      ...proximoComDatasExpansao,
      resultados: { ...proximoComDatasExpansao.resultados, ...recalcularResultados(proximoComDatasExpansao) },
    });
  }

  function atualizarLista(campo, index, parcial) {
    const lista = [...(form[campo] || [])];
    lista[index] = { ...lista[index], ...parcial };
    atualizarFormulario({ [campo]: lista });
  }

  function atualizarGranulometria(campo, valor) {
    atualizarFormulario({ granulometria: { ...form.granulometria, [campo]: valor } });
  }

  function atualizarLimites(secao, valor) {
    atualizarFormulario({ limites: { ...form.limites, [secao]: valor } });
  }

  function selecionarCliente(cliente) {
    atualizarFormulario({
      cliente: cliente?.nome || "",
      clienteId: cliente?.id || "",
      empresa: cliente?.nome || "",
      obra: "",
      obraId: "",
      material: "",
      materialId: "",
      materialCodigo: "",
      materialFornecedor: "",
      localColeta: "",
    });
    carregarObras(cliente?.id);
  }

  function selecionarObra(obraId) {
    const obra = obras.find((item) => item.id === obraId);
    atualizarFormulario({
      obra: obra?.nome || "",
      obraId: obra?.id || "",
      material: "",
      materialId: "",
      materialCodigo: "",
      materialFornecedor: "",
      localColeta: "",
    });
  }

  function selecionarMaterial(material) {
    atualizarFormulario({
      material: material?.Descricao || "",
      materialId: material?.id || "",
      materialCodigo: material?.codigo || "",
      materialFornecedor: material?.Fornecedor || "",
      localColeta: material?.LocalColeta || "",
    });
  }

 async function salvar() {
  if (salvandoRef.current) return;

  if (!form.clienteId) {
    alert("Selecione o cliente antes de salvar.");
    return;
  }

  if (!modoEdicao && !form.materialId) {
    alert("Selecione um material cadastrado antes de salvar.");
    return;
  }

  const user = auth.currentUser;

  if (!user) {
    alert("Usuário não autenticado.");
    return;
  }

  salvandoRef.current = true;
  setSalvando(true);

  try {
    const userSnap = await getDoc(doc(db, "users", user.uid));
    const userData = userSnap.data() || {};

    const usuarioRegistro = {
      uid: user.uid,
      nome: userData.nome || user.displayName || user.email || "",
      email: user.email || "",
      tipo: userData.tipo || "",
    };
    const codigo = modoEdicao
      ? form.codigo
      : form.codigo || (await gerarSequencial({
          chave: "compactacao",
          escopo: new Date(form.dataEnsaio || new Date()).getFullYear(),
          formato: {
            padding: 3,
            ano: new Date(form.dataEnsaio || new Date()).getFullYear(),
            template: "RE-EC-{numero}/{ano}",
          },
          valorMinimo: obterMaiorSequencial(ensaios, /RE-EC-(\d+)\/\d{4}/),
        })).codigo;

    const dados = {
      ...form,
      codigo,
      resultados: { ...form.resultados, ...recalcularResultados(form) },
      atualizadoEm: new Date(),
      updatedBy: usuarioRegistro,
    };

    if (modoEdicao && itemEditando?.id) {
      await atualizarDocumentoComMesclagem({
        db,
        referencia: doc(db, COLLECTION_COMPACTACAO, itemEditando.id),
        original: itemEditando,
        atual: { ...itemEditando, ...dados },
        metadados: { atualizadoEm: dados.atualizadoEm, updatedBy: dados.updatedBy },
      });
    } else {
      await addDoc(collection(db, COLLECTION_COMPACTACAO), aplicarTenantAtual({
        ...dados,
        criadoEm: new Date(),
        createdBy: usuarioRegistro,
        createdByUid: user.uid,
      }));
    }

    try {
      await carregarDados();
    } catch (error) {
      console.error("Ensaio salvo, mas a lista não pôde ser atualizada:", error);
    }

    setOpen(false);
  } catch (error) {
    console.error("Erro ao salvar ensaio de compactação:", error);
    alert("Não foi possível salvar o ensaio. Tente novamente.");
  } finally {
    salvandoRef.current = false;
    setSalvando(false);
  }
}

  const ensaiosFiltrados = useMemo(() => {
    const termo = normalizar(filtro);
    if (!termo) return ensaios;
    return ensaios.filter((item) =>
      [item.codigo, item.cliente, item.obra, item.material, item.camadaEstudo, item.registroNumero].some((campo) =>
        contemTextoPesquisa(normalizar(campo), termo)
      )
    );
  }, [ensaios, filtro]);

  const dadosCompactacao = useMemo(
    () =>
      (form.compactacao || [])
        .map((ponto) => ({
          umidade: numero(ponto.umidade),
          densidadeSeca: numero(ponto.densidadeSeca),
          densidadeUmida: numero(ponto.densidadeUmida),
        }))
        .filter((ponto) => ponto.umidade !== null && (ponto.densidadeSeca !== null || ponto.densidadeUmida !== null)),
    [form.compactacao]
  );

  const dadosCbr = useMemo(
    () =>
      (form.cbr || [])
        .map((linha) => ({ cilindro: linha.cilindro || linha.id, isc: numero(linha.isc) }))
        .filter((linha) => linha.isc !== null),
    [form.cbr]
  );

  const dadosExpansao = useMemo(
    () =>
      (form.expansao || [])
        .map((linha) => ({ cilindro: linha.cilindro || linha.id, expansao: numero(linha.expansao) }))
        .filter((linha) => linha.expansao !== null),
    [form.expansao]
  );

  const capsulasHigroscopicas = capsulas.filter((item) => item.tipo === "higroscopica");
  const capsulasLimites = capsulas.filter((item) => item.tipo === "limite_liquidez" || item.tipo === "limite_plasticidade");

  if (visualizando) {
    return (
      <RelatorioCompactacaoPrint
        ensaio={visualizando}
        onVoltar={() => setVisualizando(null)}
      />
    );
  }

  return (
    <>
      <Navbar />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Typography variant="h6" fontWeight={700}>Ensaio de Compactação</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Cadastro completo com compactação, granulometria, CBR, expansão e limites físicos</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
          <Button variant="contained" onClick={abrirNovo}>+ Novo Ensaio</Button>
          <TextField size="small" label="Pesquisar" value={filtro} onChange={(e) => setFiltro(e.target.value)} sx={{ flex: 1 }} />
          <TextField size="small" label="Qtd" value={ensaiosFiltrados.length} InputProps={{ readOnly: true }} sx={{ width: 120 }} />
        </Stack>
        </Paper>

        <Paper sx={{ overflow: "auto", border: "1px solid #d7dde8", boxShadow: "none" }}>
          <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 980 }}>
            <TableHead sx={{ backgroundColor: "#f1f5f9" }}>
              <TableRow>
                <TableCell sx={{ width: 70, whiteSpace: "nowrap" }}><strong>REG.</strong></TableCell>
                <TableCell>Cliente / Obra</TableCell>
                <TableCell>Camada/Estudo</TableCell>
                <TableCell>Material</TableCell>
                <TableCell>Data</TableCell>
                <TableCell align="center" sx={{ width: 82, px: 1 }}>Dens. máx.</TableCell>
                <TableCell align="center" sx={{ width: 88, px: 1 }}>Umidade ótima</TableCell>
                <TableCell align="center">I.S.C.</TableCell>
                <TableCell align="center" sx={{ width: 140, minWidth: 140, whiteSpace: "nowrap" }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ensaiosFiltrados.map((item) => (
                <TableRow key={item.id} hover sx={{ cursor: "pointer" }} onClick={() => history.push(`/app/ensaio-compactacao/${item.id}`)}>
                  <TableCell sx={{ width: 70, whiteSpace: "nowrap" }}>{item.registroNumero || item.codigo || "-"}</TableCell>
                  <TableCell>
                    <div className="lista-dado-principal">{item.empresa || item.cliente || "-"}</div>
                    <div className="lista-dado-secundario">Obra: {item.obra || "-"}</div>
                  </TableCell>
                  <TableCell className="lista-dado-secundario">{item.camadaEstudo || "-"}</TableCell>
                  <TableCell className="lista-dado-destaque">{item.material || "-"}</TableCell>
                  <TableCell className="lista-dado-secundario">{formatarDataBR(item.dataEnsaio)}</TableCell>
                  <TableCell align="center" sx={{ width: 82, px: 1 }}>{item.resultados?.densidadeMaxima || "-"}</TableCell>
                  <TableCell align="center" sx={{ width: 88, px: 1 }}>{item.resultados?.umidadeOtima || "-"}</TableCell>
                  <TableCell align="center">{item.resultados?.isc || "-"}</TableCell>
                  <TableCell align="center" sx={{ width: 140, minWidth: 140, whiteSpace: "nowrap" }}>
                    <Tooltip title="Visualizar relatório">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          setVisualizando(item);
                        }}
                      >
                        <VisibilityIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar ensaio">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          editar(item);
                        }}
                      >
                        <EditIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                    <HistoricoCadastro colecao="tblEnsaiosCompactacao" documentoId={item.id} registro={item} />
                  </TableCell>
                </TableRow>
              ))}
              {carregandoLista && <TableRow><TableCell colSpan={9}><Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4 }}><CircularProgress /><Typography color="text.secondary">Carregando registros...</Typography></Box></TableCell></TableRow>}
              {!carregandoLista && !ensaiosFiltrados.length && (
                <TableRow><TableCell colSpan={9} align="center">Nenhum ensaio cadastrado.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog open={open} onClose={() => !salvando && setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Nova Identificação do Ensaio de Compactação</DialogTitle>
        <DialogContent dividers>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <TextField label="Código" value={form.codigo} onChange={(e) => atualizarFormulario({ codigo: e.target.value })} fullWidth />
                  <TextField label="Registro Nº" value={form.registroNumero} onChange={(e) => atualizarFormulario({ registroNumero: e.target.value })} fullWidth />
                  <TextField label="Data do ensaio" type="date" InputLabelProps={{ shrink: true }} value={form.dataEnsaio} onChange={(e) => atualizarFormulario({ dataEnsaio: e.target.value })} fullWidth />
                </Stack>
                <Autocomplete
                  options={clientes}
                  getOptionLabel={(option) => option?.nome || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={clientes.find((cliente) => cliente.id === form.clienteId) || null}
                  onChange={(event, novoValor) => selecionarCliente(novoValor)}
                  renderInput={(params) => <TextField {...params} label="Empresa/Cliente" fullWidth />}
                />
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <TextField select label="Obra" value={form.obraId || ""} onChange={(e) => selecionarObra(e.target.value)} fullWidth disabled={!form.clienteId}>
                    {obras.map((obra) => <MenuItem key={obra.id} value={obra.id}>{obra.nome}</MenuItem>)}
                  </TextField>
                  <TextField label="Camada/Estudo" value={form.camadaEstudo} onChange={(e) => atualizarFormulario({ camadaEstudo: e.target.value })} fullWidth />
                  <Autocomplete
                    options={materiaisDisponiveis}
                    getOptionLabel={(option) =>
                      [option?.codigo, option?.Descricao, option?.Fornecedor].filter(Boolean).join(" - ")
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={materialSelecionado}
                    onChange={(event, novoValor) => selecionarMaterial(novoValor)}
                    disabled={!form.obraId}
                    noOptionsText="Nenhum material da categoria Solos cadastrado para esta obra"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Material"
                        helperText="Selecione um material cadastrado para a obra."
                        fullWidth
                      />
                    )}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <TextField label="Local de coleta" value={form.localColeta} onChange={(e) => atualizarFormulario({ localColeta: e.target.value })} fullWidth />
                  <TextField label="Operador" value={form.operador} onChange={(e) => atualizarFormulario({ operador: e.target.value })} fullWidth />
                </Stack>
                <TextField label="Observação" value={form.observacao} onChange={(e) => atualizarFormulario({ observacao: e.target.value })} multiline minRows={3} fullWidth />
              </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={salvando}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {false && (
      <Dialog open={false} onClose={() => setOpen(false)} fullWidth maxWidth="xl">
        <DialogTitle>{modoEdicao ? "Editar Ensaio de Compactação" : "Novo Ensaio de Compactação"}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>

            {tab === 1 && (
              <Stack spacing={2}>
                <Typography fontWeight={700}>Compactação</Typography>
                <Paper sx={{ overflow: "auto", border: "1px solid #d7dde8", boxShadow: "none" }}>
                  <Table size="small" sx={{ minWidth: 980 }}>
                    <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell align="center">Ponto</TableCell>
                        <TableCell>Cilindro</TableCell>
                        <TableCell align="center">Golpes</TableCell>
                        <TableCell align="center">Água</TableCell>
                        <TableCell align="center">Peso solo úmido + molde</TableCell>
                        <TableCell align="center">Umidade (%)</TableCell>
                        <TableCell align="center">Densidade úmida</TableCell>
                        <TableCell align="center">Densidade seca</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(form.compactacao || []).map((linha, index) => (
                        <TableRow key={linha.id || index}>
                          <TableCell align="center">{index + 1}</TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            <TextField
                              select
                              variant="standard"
                              value={linha.cilindroId || ""}
                              onChange={(e) => {
                                const cilindro = selecionarPorId(cilindros, e.target.value);
                                atualizarLista("compactacao", index, calcularPontoCompactacao({ ...linha, cilindroId: e.target.value }, cilindro));
                              }}
                              fullWidth
                            >
                              {cilindros.map((cilindro) => <MenuItem key={cilindro.id} value={cilindro.id}>{cilindro.numero}</MenuItem>)}
                            </TextField>
                          </TableCell>
                          <TableCell>{campoTabela(linha.golpes, (valor) => atualizarLista("compactacao", index, { golpes: valor }))}</TableCell>
                          <TableCell>{campoTabela(linha.agua, (valor) => atualizarLista("compactacao", index, { agua: valor }))}</TableCell>
                          <TableCell>{campoTabela(linha.pesoSoloUmidoMolde, (valor) => {
                            const cilindro = selecionarPorId(cilindros, linha.cilindroId);
                            atualizarLista("compactacao", index, calcularPontoCompactacao({ ...linha, pesoSoloUmidoMolde: valor }, cilindro));
                          })}</TableCell>
                          <TableCell>{campoTabela(linha.umidade, (valor) => {
                            const cilindro = selecionarPorId(cilindros, linha.cilindroId);
                            atualizarLista("compactacao", index, calcularPontoCompactacao({ ...linha, umidade: valor }, cilindro));
                          })}</TableCell>
                          <TableCell>{campoTabela(linha.densidadeUmida, (valor) => atualizarLista("compactacao", index, { densidadeUmida: valor }))}</TableCell>
                          <TableCell>{campoTabela(linha.densidadeSeca, (valor) => atualizarLista("compactacao", index, { densidadeSeca: valor }))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <TextField label="Densidade máxima" value={form.resultados?.densidadeMaxima || ""} onChange={(e) => atualizarFormulario({ resultados: { ...form.resultados, densidadeMaxima: e.target.value } })} fullWidth />
                  <TextField label="Umidade ótima" value={form.resultados?.umidadeOtima || ""} onChange={(e) => atualizarFormulario({ resultados: { ...form.resultados, umidadeOtima: e.target.value } })} fullWidth />
                </Stack>
              </Stack>
            )}

            {tab === 2 && (
              <Stack spacing={3}>
                <Typography fontWeight={700}>Ensaio de Expansão</Typography>
                <Paper sx={{ overflow: "auto", border: "1px solid #d7dde8", boxShadow: "none" }}>
                  <Table size="small" sx={{ minWidth: 1450 }}>
                    <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell>Cilindro</TableCell>
                        <TableCell align="center">Data inicial</TableCell>
                        <TableCell align="center">Hora inicial</TableCell>
                        <TableCell align="center">Leitura inicial</TableCell>
                        <TableCell align="center">24 h</TableCell>
                        <TableCell align="center">48 h</TableCell>
                        <TableCell align="center">72 h</TableCell>
                        <TableCell align="center">Data final</TableCell>
                        <TableCell align="center">Hora final</TableCell>
                        <TableCell align="center">Leitura final</TableCell>
                        <TableCell align="center">Diferença</TableCell>
                        <TableCell align="center">Expansão (%)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(form.expansao || []).map((linha, index) => (
                        <TableRow key={linha.id || index}>
                          <TableCell sx={{ minWidth: 130 }}>{campoTabela(linha.cilindro, (valor) => atualizarLista("expansao", index, { cilindro: valor }))}</TableCell>
                          <TableCell>{campoTabela(linha.dataInicial, () => {}, { type: "date", disabled: true, InputLabelProps: { shrink: true } })}</TableCell>
                          <TableCell>{campoTabela(linha.horaInicial, (valor) => atualizarLista("expansao", index, { horaInicial: valor }), { type: "time" })}</TableCell>
                          <TableCell>{campoTabela(linha.leituraInicial, (valor) => atualizarLista("expansao", index, { leituraInicial: valor }))}</TableCell>
                          {[0, 1, 2].map((leituraIndex) => {
                            const leitura = linha.leiturasDiarias?.[leituraIndex] || {};
                            const leiturasDiarias = linha.leiturasDiarias || [];
                            return (
                              <TableCell key={leituraIndex} sx={{ minWidth: 155 }}>
                                <Stack direction="row" spacing={0.5}>
                                  {campoTabela(leitura.hora, (valor) => {
                                    const proximaLista = [...leiturasDiarias];
                                    proximaLista[leituraIndex] = { ...leitura, hora: valor };
                                    atualizarLista("expansao", index, { leiturasDiarias: proximaLista });
                                  }, { type: "time" })}
                                  {campoTabela(leitura.leitura, (valor) => {
                                    const proximaLista = [...leiturasDiarias];
                                    proximaLista[leituraIndex] = { ...leitura, leitura: valor };
                                    atualizarLista("expansao", index, { leiturasDiarias: proximaLista });
                                  })}
                                </Stack>
                              </TableCell>
                            );
                          })}
                          <TableCell>{campoTabela(linha.dataFinal, () => {}, { type: "date", disabled: true })}</TableCell>
                          <TableCell>{campoTabela(linha.horaFinal, (valor) => atualizarLista("expansao", index, { horaFinal: valor }), { type: "time" })}</TableCell>
                          <TableCell>{campoTabela(linha.leituraFinal, (valor) => atualizarLista("expansao", index, { leituraFinal: valor }))}</TableCell>
                          <TableCell>{campoTabela(linha.diferenca, (valor) => atualizarLista("expansao", index, { diferenca: valor }))}</TableCell>
                          <TableCell>{campoTabela(linha.expansao, (valor) => atualizarLista("expansao", index, { expansao: valor }))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>

                <Typography fontWeight={700}>Penetração CBR</Typography>
                <Paper sx={{ overflow: "auto", border: "1px solid #d7dde8", boxShadow: "none" }}>
                  <Table size="small" sx={{ minWidth: 1300 }}>
                    <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell>Cilindro</TableCell>
                        {penetracoesCBR.map((ponto) => <TableCell key={ponto.mm} align="center">{ponto.mm} mm</TableCell>)}
                        <TableCell align="center">I.S.C. 2,54</TableCell>
                        <TableCell align="center">I.S.C. 5,08</TableCell>
                        <TableCell align="center">I.S.C.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(form.cbr || []).map((linha, index) => (
                        <TableRow key={linha.id || index}>
                          <TableCell sx={{ minWidth: 110 }}>{campoTabela(linha.cilindro, (valor) => atualizarLista("cbr", index, { cilindro: valor }))}</TableCell>
                          {(linha.leituras || []).map((leitura, leituraIndex) => (
                            <TableCell key={leitura.mm} sx={{ minWidth: 72 }}>
                              {campoTabela(leitura.pressao || leitura.leitura, (valor) => {
                                const leituras = [...(linha.leituras || [])];
                                leituras[leituraIndex] = { ...leituras[leituraIndex], pressao: valor };
                                atualizarLista("cbr", index, { leituras });
                              })}
                            </TableCell>
                          ))}
                          <TableCell>{campoTabela(linha.isc254, (valor) => atualizarLista("cbr", index, { isc254: valor }))}</TableCell>
                          <TableCell>{campoTabela(linha.isc508, (valor) => atualizarLista("cbr", index, { isc508: valor }))}</TableCell>
                          <TableCell>{campoTabela(linha.isc, (valor) => atualizarLista("cbr", index, { isc: valor }))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Stack>
            )}

            {tab === 3 && (
              <Stack spacing={3}>
                <Typography fontWeight={700}>Umidade Higroscópica</Typography>
                <Paper sx={{ overflow: "auto", border: "1px solid #d7dde8", boxShadow: "none" }}>
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell>Cápsula</TableCell>
                        <TableCell align="center">Cápsula + solo + água</TableCell>
                        <TableCell align="center">Cápsula + solo</TableCell>
                        <TableCell align="center">Água</TableCell>
                        <TableCell align="center">Cápsula</TableCell>
                        <TableCell align="center">Solo</TableCell>
                        <TableCell align="center">% Umidade</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(form.granulometria.umidadeHigroscopica || []).map((linha, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ minWidth: 160 }}>
                            <TextField
                              select
                              variant="standard"
                              value={linha.capsulaId || ""}
                              onChange={(e) => {
                                const capsula = selecionarPorId(capsulasHigroscopicas, e.target.value);
                                const lista = [...form.granulometria.umidadeHigroscopica];
                                lista[index] = { ...linha, capsulaId: e.target.value, capsula: capsula?.numero || "", pesoCapsula: capsula?.pesoCapsula || "" };
                                atualizarGranulometria("umidadeHigroscopica", lista);
                              }}
                              fullWidth
                            >
                              {capsulasHigroscopicas.map((capsula) => <MenuItem key={capsula.id} value={capsula.id}>{capsula.numero}</MenuItem>)}
                            </TextField>
                          </TableCell>
                          {["capsulaSoloAgua", "capsulaSolo", "agua", "pesoCapsula", "solo", "umidade"].map((campo) => (
                            <TableCell key={campo}>{campoTabela(linha[campo], (valor) => {
                              const lista = [...form.granulometria.umidadeHigroscopica];
                              lista[index] = { ...lista[index], [campo]: valor };
                              atualizarGranulometria("umidadeHigroscopica", lista);
                            })}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>

                <Typography fontWeight={700}>Peneiramento</Typography>
                <Paper sx={{ overflow: "auto", border: "1px solid #d7dde8", boxShadow: "none" }}>
                  <Table size="small" sx={{ minWidth: 860 }}>
                    <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell>Peneira</TableCell>
                        <TableCell align="center">mm</TableCell>
                        <TableCell align="center">Peso retido</TableCell>
                        <TableCell align="center">% retido</TableCell>
                        <TableCell align="center">% acumulado</TableCell>
                        <TableCell align="center">% que passa</TableCell>
                        <TableCell align="center">Faixa DNIT</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(form.granulometria.peneiramento || []).map((linha, index) => (
                        <TableRow key={`${linha.nome}-${index}`}>
                          <TableCell>{linha.nome}</TableCell>
                          <TableCell align="center">{linha.abertura}</TableCell>
                          {["pesoRetido", "percentualRetido", "acumulado", "percentualPassante", "faixaDnit"].map((campo) => (
                            <TableCell key={campo}>{campoTabela(linha[campo], (valor) => {
                              const lista = [...form.granulometria.peneiramento];
                              lista[index] = { ...lista[index], [campo]: valor };
                              atualizarGranulometria("peneiramento", lista);
                            })}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <TextField label='Classificação "IG"' value={form.granulometria.classificacaoIg || ""} onChange={(e) => atualizarGranulometria("classificacaoIg", e.target.value)} fullWidth />
                  <TextField label='Classificação "TRB"' value={form.granulometria.classificacaoTrb || ""} onChange={(e) => atualizarGranulometria("classificacaoTrb", e.target.value)} fullWidth />
                  <TextField label='Classificação "SUCS"' value={form.granulometria.classificacaoSucs || ""} onChange={(e) => atualizarGranulometria("classificacaoSucs", e.target.value)} fullWidth />
                </Stack>
              </Stack>
            )}

            {tab === 4 && (
              <Stack spacing={3}>
                <Typography fontWeight={700}>Limite de Liquidez</Typography>
                <TabelaLimites
                  linhas={form.limites.liquidez || []}
                  capsulas={capsulasLimites}
                  comGolpes
                  onChange={(linhas) => atualizarLimites("liquidez", linhas)}
                />
                <Typography fontWeight={700}>Limite de Plasticidade</Typography>
                <TabelaLimites
                  linhas={form.limites.plasticidade || []}
                  capsulas={capsulasLimites}
                  onChange={(linhas) => atualizarLimites("plasticidade", linhas)}
                />
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <TextField label="Limite de Liquidez (LL)" value={form.limites.resultados?.limiteLiquidez || ""} onChange={(e) => atualizarLimites("resultados", { ...form.limites.resultados, limiteLiquidez: e.target.value })} fullWidth />
                  <TextField label="Limite de Plasticidade (LP)" value={form.limites.resultados?.limitePlasticidade || ""} onChange={(e) => atualizarLimites("resultados", { ...form.limites.resultados, limitePlasticidade: e.target.value })} fullWidth />
                  <TextField label="Índice de Plasticidade (IP)" value={form.limites.resultados?.indicePlasticidade || ""} onChange={(e) => atualizarLimites("resultados", { ...form.limites.resultados, indicePlasticidade: e.target.value })} fullWidth />
                </Stack>
              </Stack>
            )}

            {tab === 5 && (
              <Stack spacing={3}>
                <Typography fontWeight={700}>Resultado da Compactação</Typography>
                <Paper sx={{ p: 2, border: "1px solid #d7dde8", boxShadow: "none" }}>
                  {dadosCompactacao.length ? (
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart data={dadosCompactacao}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="umidade" label={{ value: "Umidade (%)", position: "insideBottom", offset: -4 }} />
                        <YAxis yAxisId="seca" label={{ value: "Densidade seca", angle: -90, position: "insideLeft" }} />
                        <YAxis yAxisId="umida" orientation="right" label={{ value: "Densidade úmida", angle: 90, position: "insideRight" }} />
                        <ChartTooltip />
                        <Legend />
                        <Line yAxisId="seca" type="monotone" dataKey="densidadeSeca" name="Densidade seca" stroke="#0f72cf" strokeWidth={2} dot />
                        <Line yAxisId="umida" type="monotone" dataKey="densidadeUmida" name="Densidade úmida" stroke="#2e7d32" strokeWidth={2} dot />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : graficoVazio("Informe os pontos da compactação para gerar o gráfico principal.")}
                </Paper>
                <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
                  <Paper sx={{ p: 2, flex: 1, border: "1px solid #d7dde8", boxShadow: "none" }}>
                    <Typography fontWeight={700} mb={1}>CBR / I.S.C.</Typography>
                    {dadosCbr.length ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={dadosCbr}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="cilindro" />
                          <YAxis />
                          <ChartTooltip />
                          <Line type="monotone" dataKey="isc" name="I.S.C." stroke="#9c27b0" strokeWidth={2} dot />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : graficoVazio("Informe os valores de I.S.C.")}
                  </Paper>
                  <Paper sx={{ p: 2, flex: 1, border: "1px solid #d7dde8", boxShadow: "none" }}>
                    <Typography fontWeight={700} mb={1}>Expansão</Typography>
                    {dadosExpansao.length ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={dadosExpansao}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="cilindro" />
                          <YAxis />
                          <ChartTooltip />
                          <Line type="monotone" dataKey="expansao" name="Expansão (%)" stroke="#ef6c00" strokeWidth={2} dot />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : graficoVazio("Informe os valores de expansão.")}
                  </Paper>
                </Stack>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvar}>Salvar</Button>
        </DialogActions>
      </Dialog>
      )}

    </>
  );
}

function TabelaLimites({ linhas, capsulas, comGolpes = false, onChange }) {
  function atualizar(index, parcial) {
    const lista = [...linhas];
    lista[index] = { ...lista[index], ...parcial };
    onChange(lista);
  }

  return (
    <Paper sx={{ overflow: "auto", border: "1px solid #d7dde8", boxShadow: "none" }}>
      <Table size="small" sx={{ minWidth: 920 }}>
        <TableHead sx={{ backgroundColor: "#f8fafc" }}>
          <TableRow>
            <TableCell>Cápsula Nº</TableCell>
            <TableCell align="center">Tampa</TableCell>
            <TableCell align="center">Cápsula + solo + água</TableCell>
            <TableCell align="center">Cápsula + solo</TableCell>
            <TableCell align="center">Água</TableCell>
            <TableCell align="center">Cápsula</TableCell>
            <TableCell align="center">Solo</TableCell>
            <TableCell align="center">Umidade (%)</TableCell>
            {comGolpes && <TableCell align="center">Nº de golpes</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {linhas.map((linha, index) => (
            <TableRow key={linha.id || index}>
              <TableCell sx={{ minWidth: 150 }}>
                <TextField
                  select
                  variant="standard"
                  value={linha.capsulaId || ""}
                  onChange={(e) => {
                    const capsula = selecionarPorId(capsulas, e.target.value);
                    atualizar(index, {
                      capsulaId: e.target.value,
                      capsula: capsula?.numero || "",
                      pesoCapsula: capsula?.pesoCapsula || "",
                      tampa: capsula?.pesoTampa || linha.tampa || "",
                    });
                  }}
                  fullWidth
                >
                  {capsulas.map((capsula) => <MenuItem key={capsula.id} value={capsula.id}>{capsula.numero}</MenuItem>)}
                </TextField>
              </TableCell>
              {["tampa", "capsulaSoloAgua", "capsulaSolo", "agua", "pesoCapsula", "solo", "umidade"].map((campo) => (
                <TableCell key={campo}>{campoTabela(linha[campo], (valor) => atualizar(index, { [campo]: valor }))}</TableCell>
              ))}
              {comGolpes && <TableCell>{campoTabela(linha.golpes, (valor) => atualizar(index, { golpes: valor }))}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
